import { Component, ElementRef, EventEmitter, HostListener, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ApiService } from 'app/api.service';
import { AppService } from 'app/app.service';
import { Pagination } from 'app/shared/pagination';
import { CurrencyFlexiblePipe } from 'app/shared/currency-flexible.pipe';
import { DecimalPipe } from 'app/shared/decimal.pipe';
import { UIModalBaseComponent } from 'app/ui/ui-modal-base.component';
import { ModalEditProductComponent } from 'app/modal/modal-edit-product/modal-edit-product.component';
import { ModalEditWarehouseReserveItemsComponent } from 'app/modal/modal-edit-warehouse-reserve-items/modal-edit-warehouse-reserve-items.component';
import { Observable, Subscription } from 'rxjs';

@Component({
	selector: 'app-modal-global-search',
	templateUrl: './modal-global-search.component.html',
	styleUrls: ['./modal-global-search.component.scss']
})
export class ModalGlobalSearchComponent extends UIModalBaseComponent implements OnInit, OnDestroy {
	@ViewChild(ModalEditProductComponent) productEditModal: ModalEditProductComponent;
	@ViewChild(ModalEditWarehouseReserveItemsComponent) reserveItemsModal: ModalEditWarehouseReserveItemsComponent;

	searchQuery: string = '';
	searchResults: any[];
	facets: any = null;
	segments: any[] = [];
	browseCategories: any[] = [];
	totalCategories = 0;
	totalGroups = 0;
	capped = false;
	maxRecentSearches = 5;
	// Matches MIN_SEARCH_LENGTH in api-globalsearch.php - below this there's nothing worth scanning for.
	minSearchLength = 3;
	scope: any = { segment: null, category: null, subcategories: [] };
	// The last drill-down, kept so it survives a trip through a tab that has no category tree.
	treeScope: any = { segment: null, category: null, subcategories: [] };
	// Match count per tab for whatever is currently searched, so a badge never promises results a tab doesn't have.
	tabTotals: any = { products: 0, customers: 0, stock: 0, users: 0, journal: 0 };

	/**
	 * Tabs whose number is a floor rather than a total. Two things can stop a count short: the badge query gives
	 * up at its cap, and a tab's own scan stops at one too - so a category holding 37,000 products reports the
	 * groups it found in the rows it read, not what the browse tree counts over the whole catalogue.
	 *
	 * Which badges are capped is the backend's to say - only the products count is taken inside a LIMIT - so this
	 * is read from the response rather than inferred from the numbers.
	 */
	tabApproximate: any = {};

	// Whether the result count on screen is one of those floors.
	approximate = false;
	suggestions: any[] = [];
	selectedRecord: any = null;
	resultsCurrencyId = null;
	recordDetail: any = null;
	recordDetailLoading = false;
	/**
	 * Sections of the stock panel that offer reserving. Locations only: it's the section that says what is actually
	 * on the shelf in each warehouse, which is what there is to reserve - the others describe stock already spoken for.
	 */
	recordGroupActionSections = ['locations'];
	// A reservation is the one thing the panel lists that can be changed from here, so it's the only section acted on.
	recordLineActionSections = ['reservations'];
	// Held as a field rather than built in the getter: a fresh array each pass would re-create the buttons constantly.
	reservationLineActions = [
		{ id: 'edit', icon: 'wq-edit', title: 'Edit reservation', class: 'btn-outline-info' },
		{ id: 'delete', icon: 'wq-delete', title: 'Delete reservation', class: 'btn-outline-danger' }
	];
	/**
	 * Distributor copies added on this trip through the modal, as { id, quantity }, so a row says what has already
	 * been taken from it and how much. Filled from the screen behind the modal rather than on click, because a
	 * product with accessories opens the line editor and may never be added at all. Reset on open rather than on
	 * each search: the screen it reports to may have changed, but re-finding a product within the one session
	 * should still show it as added.
	 */
	addedProducts: any[] = [];
	// The product panel takes over the results area, so it's held apart from the docked record panel.
	selectedProduct: any = null;
	productDetail: any = null;
	productDetailLoading = false;

	productSectionsLoading: string[] = [];
	private productSectionsLoaded: string[] = [];
	filterSections: any[] = [];
	// Selected facet values for the active tab, as removable chips. Held as a field for the same reason
	// filterSections is - see buildFilterSections.
	filterChips: any[] = [];
	// "sectionId:value" -> { label, color } for every facet value the panel has offered, so a chip keeps its name
	// after the value drops out of the facets. See composeFilterChips.
	private filterLabels: any = {};
	recentSearches: string[] = [];
	pagination = new Pagination();
	loading = false;
	// How old the results on screen are, as a phrase for the header. A field rather than a getter because a timer
	// refreshes it: nothing else happens while the modal sits open, so a getter would read "just now" indefinitely.
	resultsAge = '';
	/**
	 * The last result set each tab loaded, with the request that produced it. Coming back to a tab whose term,
	 * filters and scope haven't changed restores this instead of searching again - the products scan is the
	 * expensive query in the modal, and a tab switch that changes nothing shouldn't pay for it twice.
	 *
	 * An entry is only ever read when its signature still matches, so there's nothing to invalidate: a stale entry
	 * simply stops answering, and is overwritten next time that tab searches.
	 */
	private tabCache: any = {};
	/**
	 * The product whose panel has to be put back once a refresh lands. A new result set closes whatever panel is
	 * open - it is stale by definition - but a refresh after an edit is the one case where the panel is exactly
	 * what should be looked at again, refetched.
	 */
	private reopenProduct = null;
	/**
	 * The same for the docked record panel, whose stock has just been reserved or released. Held as the record rather
	 * than an id and matched back by key, because the row object itself belongs to the result set being replaced.
	 */
	private reopenRecord = null;
	private resultsFetchedAt: Date = null;
	/**
	 * Bumped by every search and every tab switch. A response is only applied while it is still the latest one asked
	 * for: otherwise a slow search from the tab just left lands on the one now open - its results in the list and its
	 * total on the wrong badge - or an older term's results overwrite a newer one's.
	 */
	private searchSeq = 0;
	/**
	 * What the journal badge was last counted for - the organisation, the term and the journal's own filters. The
	 * journal count is the costliest query in the modal, and every other tab's search would otherwise repeat it for
	 * a term it has already been counted for. Cleared when the modal opens, so a reopened modal counts afresh.
	 */
	private journalCountKey: string = null;
	private ageTimer = null;
	private addedSubscription: Subscription = null;
	content;
	@ViewChild('searchBox') searchBox: ElementRef;
	// Which organisation and tab the loaded category tree belongs to, as "org:tab".
	private browseKey = null;
	// True while that tree is being refetched, so the sidebar and the browse list show a loader rather than the
	// previous tab's counts.
	browseLoading = false;
	/**
	 * The term the results on screen were found with, as opposed to `searchQuery`, which is whatever is in the box
	 * right now. Null until a search has actually been asked for, which is what keeps the browse view up - and the
	 * last results on screen - while someone is still typing.
	 */
	private submittedTerm = null;
	selectedFilters: any = {};
	@Output() searchResultsChanged: EventEmitter<any> = new EventEmitter();
	showRecentSearches = false;
	tabs: any = {
		products: { id: 'products', title: 'Products', disabled: false, icon: 'wq wq-cart-outline' },
		stock: { id: 'stock', title: 'Stock', disabled: false, icon: 'wq wq-warehouse' },
		customers: { id: 'customers', title: 'Customers', disabled: false, icon: 'wq wq-customer' },
		users: { id: 'users', title: 'Users', disabled: false, icon: 'wq wq-user' },
		journal: { id: 'journal', title: 'Journal', disabled: false, icon: 'wq wq-notes' }
	};

	// Products and stock classify their results, so they get the category tree. Customers and users don't.
	tabsWithCategoryTree = ['products', 'stock'];

	// Tabs that actually have a backend search behind them.
	tabsImplemented = ['products', 'customers', 'stock', 'users', 'journal'];

	// Tabs whose rows open the docked record panel beside the list. Products get their own full-width panel instead.
	tabsWithRecordPanel = ['customers', 'users', 'stock', 'journal'];

	// Which facet sections belong to which tab. A selection made on one tab is kept when you leave, but only the
	// sections listed here are sent with that tab's search - the rest simply don't apply there.
	tabFilterIds: any = {
		products: ['inStock', 'discontinued', 'brands', 'distributors'],
		stock: ['warehouses', 'serialised', 'allocation', 'freeStock'],
		customers: ['labels', 'openQuotes'],
		users: ['roles', 'documentTypes'],
		journal: ['contentTypes', 'recordSources', 'formats', 'authors', 'customers', 'projects']
	};

	/**
	 * Each journal record source as [singular, plural, icon]: singular for a row and the button that opens it, plural
	 * for the filter, and the icon of the menu entry the record is opened from (navigation() in api-organisation.php),
	 * so a row reads the same as the place it leads to. The backend only offers the sources this user may read, so a
	 * name listed here is never a promise.
	 */
	journalSources: any = {
		lead: ['Lead', 'Leads', 'wq-client'],
		deal: ['Deal', 'Deals', 'wq-chart'],
		quote: ['Quote', 'Quotes', 'wq-handshake'],
		sales_order: ['Sales Order', 'Sales orders', 'wq-handshake'],
		customer: ['Customer', 'Customers', 'wq-customer'],
		project: ['Project', 'Projects', 'wq-project'],
		product_log: ['Product Log', 'Product log', 'wq-product-log'],
		invoice: ['Invoice', 'Invoices', 'wq-card'],
		job: ['Job', 'Jobs', 'wq-work-order'],
		task: ['Task', 'Tasks', 'wq-task'],
		booking: ['Booking', 'Bookings', 'wq-schedule'],
		time_entry: ['Time Entry', 'Time entries', 'wq-clock'],
		team_member: ['Team Member', 'Team members', 'wq-team'],
		purchase_order: ['Purchase Order', 'Purchase orders', 'wq-page'],
		stock_reservation: ['Stock Reservation', 'Stock reservations', 'wq-warehouse'],
		stock_transfer: ['Stock Transfer', 'Stock transfers', 'wq-truck-fill']
	};

	journalSourceGroups = [
		{ id: 'sales', label: 'Sales & CRM', color: 'primary', sources: ['lead', 'deal', 'quote', 'sales_order'] },
		{ id: 'customers', label: 'Customers & Projects', color: 'danger', sources: ['customer', 'project', 'product_log', 'invoice'] },
		{ id: 'workhub', label: 'Work Hub', color: 'success', sources: ['job', 'task', 'booking', 'time_entry', 'team_member'] },
		{ id: 'procurement', label: 'Procurement & Inventory', color: 'forestgreen', sources: ['purchase_order', 'stock_reservation', 'stock_transfer'] }
	];

	tabList = [];
	activeTab = 'products';

	constructor(
		private api: ApiService,
		private app: AppService
	) {
		super();
	}

	ngOnInit(): void {
		this.loadRecentSearches();
		this.tabList.push(this.tabs.products);
		this.tabList.push(this.tabs.stock);
		this.tabList.push(this.tabs.customers);
		this.tabList.push(this.tabs.users);
		this.tabList.push(this.tabs.journal);
		this.activeTab = 'products';
		this.showRecentSearches = false;
		this.searchResults = [];
		// Half a minute is fine for a label that reads in minutes, and it costs a change detection pass.
		this.ageTimer = setInterval(() => this.updateResultsAge(), 30000);
		this.addedSubscription = this.app.globalSearchAdded.subscribe(added => {
			if (!added?.product_id) return;

			const existing = this.addedProducts.find(item => item.id === added.product_id);
			if (existing) {
				existing.quantity += added.quantity || 1;
			} else {
				this.addedProducts = this.addedProducts.concat({ id: added.product_id, quantity: added.quantity || 1 });
			}
		});
	}

	ngOnDestroy(): void {
		if (this.ageTimer) clearInterval(this.ageTimer);
		if (this.addedSubscription) this.addedSubscription.unsubscribe();
	}

	/**
	 * How old the results are, in the terms that actually matter: minutes while you're working, a clock time once
	 * it's been longer, a date once it isn't today's. The modal keeps its results when it closes, and a tab switch
	 * can put a set back that was fetched a while ago, so the age isn't something the screen would otherwise show.
	 */
	private updateResultsAge(): void {
		if (!this.resultsFetchedAt) {
			this.resultsAge = '';
			return;
		}

		const seconds = Math.floor((Date.now() - this.resultsFetchedAt.getTime()) / 1000);
		const time = this.resultsFetchedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
		const today = this.resultsFetchedAt.toDateString() === new Date().toDateString();

		if (seconds < 45) this.resultsAge = 'just now';
		else if (seconds < 3600) this.resultsAge = Math.round(seconds / 60) + ' min ago';
		else if (today) this.resultsAge = 'at ' + time;
		else this.resultsAge = this.resultsFetchedAt.toLocaleDateString() + ', ' + time;
	}

	/**
	 * Typed into the search box. Nothing is fetched: the product keyword match can't use an index, so a scan is far
	 * too expensive to spend on a term nobody has finished typing. Only the local category suggestions keep up.
	 */
	onSearchInput(): void {
		this.updateSuggestions();
	}

	// The Search button and the Enter key. Everything on screen stays as it was until this is asked for.
	search(): void {
		this.showRecentSearches = false;
		this.suggestions = [];
		if (this.canSearch && this.searchTerm) this.recordRecentSearch(this.searchTerm);
		this.rerun();
	}

	private recordRecentSearch(query: string): void {
		const index = this.recentSearches.indexOf(query);
		if (index !== -1) this.recentSearches.splice(index, 1);

		this.recentSearches.unshift(query);

		if (this.recentSearches.length > this.maxRecentSearches) {
			this.recentSearches = this.recentSearches.slice(0, this.maxRecentSearches);
		}

		localStorage.setItem('recentSearches', JSON.stringify(this.recentSearches));
	}

	private resetResults(): void {
		this.searchResults = [];
		this.facets = null;
		this.totalGroups = 0;
		this.capped = false;
		this.approximate = false;
		this.pagination.page = 1;
		// Back on the browse view, so there's no fetch to date.
		this.resultsFetchedAt = null;
		this.updateResultsAge();
		this.buildFilterSections();
	}

	// Suggestions come from the classification tree we already hold, so there's no round trip per keystroke.
	updateSuggestions(): void {
		const term = this.searchQuery.trim().toLowerCase();
		if (term.length < 2) {
			this.suggestions = [];
			return;
		}

		this.suggestions = this.browseCategories
			.filter(category => (category.category || '').toLowerCase().includes(term))
			.slice(0, 5);
	}

	applySuggestion(category): void {
		this.suggestions = [];
		this.scope = { segment: category.segment, category: category.category, subcategories: [] };
		// Kept in step so the drill-down survives a trip through a tab with no tree, the same as the sidebar's own.
		this.treeScope = this.scope;
		this.searchQuery = '';
		this.rerun();
	}

	// The sidebar drilled somewhere new - re-run with the new scope, keeping any keyword.
	scopeChanged(scope): void {
		this.scope = scope || { segment: null, category: null, subcategories: [] };
		this.treeScope = this.scope;
		this.rerun();
	}

	get hasScope() {
		return !!(this.scope.segment || this.scope.category || this.scope.subcategories?.length);
	}

	// Only the filters that actually apply to this tab count as "filtering".
	get activeFilterIds() {
		const ids = this.tabFilterIds[this.activeTab] || [];
		return ids.filter(id => (this.selectedFilters[id] || []).length > 0);
	}

	get hasFilters() {
		return this.hasScope || this.activeFilterIds.length > 0;
	}

	// Clears what is actually applied here - the scope and this tab's own filters - leaving other tabs' choices be.
	clearFilters(): void {
		this.scope = { segment: null, category: null, subcategories: [] };
		this.treeScope = this.scope;

		const next = { ...this.selectedFilters };
		this.activeFilterIds.forEach(id => delete next[id]);
		this.selectedFilters = next;

		this.rerun();
	}

	removeScope(level): void {
		if (level === 'segment') this.scope = { segment: null, category: null, subcategories: [] };
		if (level === 'category') this.scope = { ...this.scope, category: null, subcategories: [] };
		if (level === 'subcategories') this.scope = { ...this.scope, subcategories: [] };
		this.treeScope = this.scope;
		this.rerun();
	}

	clearSearch(): void {
		this.searchQuery = '';
		this.suggestions = [];
		this.rerun();
	}

	private rerun(): void {
		if (!this.canSearch) {
			this.submittedTerm = null;
			this.resetResults();
			// Nothing to search, so no tab is excluded - every badge goes back to its own total.
			this.loadSearchCounts(null);
			this.searchResultsChanged.emit(this.searchResults);
			return;
		}

		// From here the results belong to what is in the box, not to whatever was searched for before.
		this.submittedTerm = this.searchTerm;
		this.searchResults = null;
		const seq = ++this.searchSeq;
		this.performSearch(this.searchTerm).subscribe(
			(data) => {
				if (seq !== this.searchSeq) return;
				this.applyResults(data);
				this.searchResultsChanged.emit(this.searchResults);
			},
			(error) => {
				if (seq !== this.searchSeq) return;
				this.searchResults = [];
				// Nothing landed to put a panel back on top of, and it must not surface on a later search instead.
				this.reopenProduct = null;
				this.reopenRecord = null;
				this.app.notifications.showDanger(error.message);
			}
		);
	}

	/**
	 * Dispatches to the active tab's search. Products and stock also carry the category-tree scope; customers and
	 * users are filters plus the search term only.
	 */
	private performSearch(query: string): Observable<any> {
		return new Observable((observer) => {
			const done = (data) => {
				observer.next(data);
				observer.complete();
			};
			const fail = (error) => {
				observer.error(error);
				observer.complete();
			};

			if (this.activeTab === 'customers') {
				this.api.globalSearch.searchCustomers({
					query,
					labels: this.selectedFilters['labels'] || [],
					open_quotes: (this.selectedFilters['openQuotes'] || []).length > 0
				}, done, fail);
				return;
			}

			if (this.activeTab === 'journal') {
				this.api.globalSearch.searchJournal({
					query,
					content_types: this.selectedFilters['contentTypes'] || [],
					record_sources: this.selectedFilters['recordSources'] || [],
					formats: this.selectedFilters['formats'] || [],
					authors: this.selectedFilters['authors'] || [],
					customers: this.selectedFilters['customers'] || [],
					projects: this.selectedFilters['projects'] || []
				}, done, fail);
				return;
			}

			if (this.activeTab === 'users') {
				this.api.globalSearch.searchUsers({
					query,
					roles: this.selectedFilters['roles'] || [],
					document_types: this.selectedFilters['documentTypes'] || []
				}, done, fail);
				return;
			}

			if (this.activeTab === 'stock') {
				this.api.globalSearch.searchStock({
					query,
					warehouses: this.selectedFilters['warehouses'] || [],
					serialised: this.selectedFilters['serialised'] || [],
					allocation: this.selectedFilters['allocation'] || [],
					free_stock: (this.selectedFilters['freeStock'] || []).length > 0,
					segment: this.scope.segment,
					category: this.scope.category,
					subcategories: this.scope.subcategories
				}, done, fail);
				return;
			}

			this.api.globalSearch.searchProducts({
				query,
				in_stock: (this.selectedFilters['inStock'] || []).length > 0,
				include_discontinued: (this.selectedFilters['discontinued'] || []).length > 0,
				brands: this.selectedFilters['brands'] || [],
				catalogue_ids: this.selectedFilters['distributors'] || [],
				segment: this.scope.segment,
				category: this.scope.category,
				subcategories: this.scope.subcategories
			}, done, fail);
		});
	}

	recentSearch(query): void {
		this.searchQuery = query;
		this.showRecentSearches = false;
		this.suggestions = [];
		this.rerun();
	}

	showRecentSearch() {
		this.showRecentSearches = true;
	}

	hideRecentSearch() {
		this.showRecentSearches = false;
	}

	// Recent searches and suggestions hang off the search box, so anything clicked elsewhere dismisses them.
	@HostListener('document:click', ['$event'])
	onDocumentClick(event) {
		if (this.searchBox?.nativeElement.contains(event.target)) return;

		this.showRecentSearches = false;
		this.suggestions = [];
	}

	// Only offered when the box is focused and empty - once you're typing, the category suggestions take over.
	get showsRecentSearches() {
		return this.showRecentSearches && !this.searchTerm && this.recentSearches.length > 0;
	}

	loadRecentSearches(): void {
		const recentSearchesStr = localStorage.getItem('recentSearches');
		if (recentSearchesStr) {
			// Slice on the way in too - older versions stored more than we show.
			this.recentSearches = JSON.parse(recentSearchesStr).slice(0, this.maxRecentSearches);
		}
	}

	/**
	 * The browse view - category groups down the side and the browsable category list - covers the organisation's
	 * whole catalogue, so it's loaded once per organisation and tab. This runs on open, not on init: the modal is
	 * constructed before an organisation is in context.
	 *
	 * Cached per tab as well as per organisation, because the counts are of whatever that tab lists: catalogue
	 * products on products, stock items on stock.
	 */
	loadBrowse(done = null): void {
		const org = this.app.org;
		const tab = this.browseTab;
		const key = org + ':' + tab;

		// Already loaded for this organisation and tab, or no organisation to load for.
		if (!org || key === this.browseKey) {
			if (done) done();
			return;
		}

		this.browseKey = key;
		this.browseLoading = true;

		this.api.globalSearch.browse(
			tab,
			(data) => {
				// A later tab change may have landed while this was in flight; its tree wins.
				if (this.browseKey !== key) return;
				this.segments = data.segments || [];
				this.browseCategories = data.categories || [];
				this.totalCategories = data.total_categories || 0;
				this.browseLoading = false;
				if (done) done();
			},
			() => {
				if (this.browseKey !== key) return;
				this.segments = [];
				this.browseCategories = [];
				this.totalCategories = 0;
				this.browseKey = null;
				this.browseLoading = false;
				// Still open - a failed browse shouldn't stop someone searching.
				if (done) done();
			}
		);
	}

	// Which tab's tree to load. Tabs without a tree keep whichever is already loaded rather than fetching one.
	get browseTab() {
		if (this.showsCategoryTree) return this.activeTab;
		return this.browseKey ? this.browseKey.split(':')[1] : 'products';
	}

	// What the browse list is counting, so a row reads "3,158 products" or "12 stock items".
	/**
	 * What the browse tree's numbers count. On products that is union groups - one per row the search will show,
	 * not one per product: a group carrying three distributors' copies is one number here and one row there. It
	 * used to read "products", which promised a count of the copies underneath and was always lower than the truth.
	 */
	get browseCountLabel() {
		return this.activeTab === 'stock' ? 'stock items' : 'results';
	}

	private applyResults(data): void {
		const fetchedAt = new Date();

		this.renderResults(data, fetchedAt);

		// The page index belongs to the list that's being replaced - Pagination only clamps a too-high page down, so
		// without this you land mid-way through a different tab's results for no visible reason.
		this.pagination.page = 1;

		// Kept so coming back to this tab doesn't have to search for it again. See tabCache.
		this.tabCache[this.activeTab] = { signature: this.searchSignature(), data, page: 1, fetchedAt };

		// The journal's own search has just put its total on the badge, so the other tabs needn't count it again.
		if (this.activeTab === 'journal') this.journalCountKey = this.journalCountKeyFor(this.searchTerm, this.selectedFilters);

		// The active tab's own search has landed and rendered; the other badges follow.
		this.loadSearchCounts();
	}

	/**
	 * Draws a result set into the list, whether it just arrived or came back out of tabCache. The fetch time travels
	 * with it, so restored results report when they were actually fetched rather than when they were put back.
	 */
	private renderResults(data, fetchedAt: Date): void {
		this.resultsFetchedAt = fetchedAt;
		this.updateResultsAge();

		// A result set makes whatever was open behind it stale.
		this.closeRecord();
		this.closeProduct();
		this.resultsCurrencyId = data.currency_id || null;
		this.searchResults = data.list || [];
		this.facets = data.facets || null;
		// Products count union groups; the other tabs count rows.
		const total = data.total_groups !== undefined ? data.total_groups : (data.total || 0);
		this.capped = !!data.capped;
		this.approximate = !!data.approximate;

		// Under the cap the count is real. Over it, the scan stopped at a number of rows and those rows folded into
		// fewer groups, so the count is what the sample found and the "+" beside it says there is more. The row cap
		// itself is not the number to show - 2,000 rows fold into far fewer groups, so quoting it over-reports.
		this.totalGroups = total;
		this.tabTotals = { ...this.tabTotals, [this.activeTab]: this.totalGroups };
		this.tabApproximate = { ...this.tabApproximate, [this.activeTab]: this.approximate };
		this.buildFilterSections();

		// The panel closeProduct() just cleared belongs to a product that was edited, not to the old results: it goes
		// back up on the new ones. Cleared as it's used, so only the refresh that asked for it gets it.
		if (this.reopenProduct) {
			const id = this.reopenProduct;
			this.reopenProduct = null;
			this.selectProduct(id);
		}

		// The record panel goes back up the same way, on the refreshed row rather than the one it was opened from.
		// A row that no longer matches the search simply doesn't come back, which is what closing it would have said.
		if (this.reopenRecord) {
			const key = this.recordKey(this.reopenRecord);
			this.reopenRecord = null;

			const row = (this.searchResults || []).find(result => this.recordKey(result) === key);
			if (row) this.selectRecord(row);
		}
	}

	/**
	 * Puts the active tab's last results back on screen, and says whether it could: an entry only answers for the
	 * exact request that produced it, so a stale one is simply a miss and the caller searches as normal.
	 */
	private restoreResults(): boolean {
		const cached = this.tabCache[this.activeTab];
		if (!cached || cached.signature !== this.searchSignature()) return false;

		this.submittedTerm = this.searchTerm;

		this.renderResults(cached.data, cached.fetchedAt);
		this.pagination.page = cached.page || 1;

		// No loadSearchCounts: the badges are already showing counts for this term, which is what made this a hit.
		return true;
	}

	/**
	 * Everything a tab's results depend on: the term, the tab's own filters, and - for the tabs that have one - the
	 * drill-down. Deliberately per-tab, so ticking a brand on Products doesn't throw away the Customers results it
	 * has no bearing on.
	 */
	private searchSignature(tab: string = this.activeTab): string {
		const filters = {};
		(this.tabFilterIds[tab] || []).forEach(id => {
			const values = this.selectedFilters[id] || [];
			if (values.length) filters[id] = values;
		});

		// treeScope rather than scope, for the reason given in loadSearchCounts: the live scope is blanked while a
		// tab without a tree is open, and a signature has to mean the same thing from whichever tab it's taken.
		const scope = this.tabsWithCategoryTree.indexOf(tab) !== -1
			? {
				segment: this.treeScope.segment,
				category: this.treeScope.category,
				subcategories: this.treeScope.subcategories || []
			}
			: null;

		return JSON.stringify({ term: this.searchTerm, filters, scope });
	}

	/**
	 * Opens straight away and fills in behind: the category tree is a full-catalogue aggregate and the products
	 * count is another, so waiting on either would leave the button feeling dead for as long as they take. The
	 * sidebar and the browse list show their own loaders until the tree lands.
	 */
	open(_data: any) {
		this.addedProducts = [];
		// Notes may have been added since it last opened, so the journal badge is counted again.
		this.journalCountKey = null;
		this.modal.open();

		// Nothing is being searched yet, so no tab is excluded - all four badges come from this one call.
		this.loadSearchCounts(null);
		this.loadBrowse();
	}

	/**
	 * The other tabs' match counts for whatever is currently being searched, so a badge never promises results the
	 * tab doesn't have. The active tab is excluded: its count comes from its own search, and asking for it again
	 * would scan the catalogue twice for the same keyword.
	 *
	 * Always fired after the active tab's search, never before it - the results are what the user is waiting on.
	 */
	loadSearchCounts(exclude: string = this.activeTab): void {
		if (!this.app.org) return;

		// treeScope, not scope: the drill-down belongs to the classification tree that products and stock share, and
		// it's held aside - not dropped - while a tab without a tree is open. Sending the live scope would report
		// the whole catalogue for Products the moment you stepped onto Customers or Users, which isn't what that
		// tab would actually show when you went back to it.
		// The whole selection goes over, and each tab's count applies only the sections that belong to it - the same
		// split tabFilterIds makes here. A filter ticked on one tab says nothing about another, but it does still
		// apply to its own tab, so leaving it out made that tab's badge jump the moment you stepped away.
		const request = {
			query: this.searchTerm,
			exclude: exclude || '',
			segment: this.treeScope.segment,
			category: this.treeScope.category,
			subcategories: this.treeScope.subcategories,
			filters: this.selectedFilters || {}
		};

		const applyCounts = (data) => {
			// A later search or filter change may have landed while this was in flight; its counts win.
			if (request.query !== this.searchTerm) return;
			if (JSON.stringify(request.filters) !== JSON.stringify(this.selectedFilters || {})) return;
			const { approximate: capped, ...totals } = data || {};
			this.tabTotals = { ...this.tabTotals, ...totals };

			// A badge that reached the cap is a floor, and the backend says which ones did. The active tab
			// keeps whatever its own results said.
			const approximate = { ...this.tabApproximate };
			Object.keys(totals).forEach(tab => {
				approximate[tab] = !!(capped || {})[tab];
			});
			this.tabApproximate = approximate;
		};

		this.api.globalSearch.searchCounts(request, applyCounts, () => null);

		// The journal's count is the slowest - it is the tab's own scan across every source - so it is asked for
		// alongside the others rather than as part of them, and fills in when it lands. Only when the term or the
		// journal's filters have changed since it was last counted: the badge already says what it would.
		const journalKey = this.journalCountKeyFor(request.query, request.filters);
		if (exclude !== 'journal' && journalKey !== this.journalCountKey) {
			this.journalCountKey = journalKey;
			this.api.globalSearch.journalCount({ query: request.query, filters: request.filters }, applyCounts, () => {
				// A failed count is retried on the next search rather than taken as counted.
				if (this.journalCountKey === journalKey) this.journalCountKey = null;
			});
		}
	}

	private journalCountKeyFor(query: string, filters: any) {
		const journalFilters = (this.tabFilterIds.journal || []).map(id => (filters || {})[id] || []);
		return JSON.stringify([this.app.org, query, journalFilters]);
	}

	saveButton() {
		this.modal.close({
			content: this.content
		});
	}

	// Filtering happens on the backend: a union group survives if any of its copies matches, and the copy shown as the
	// main product has to be re-elected from the ones that survived.
	applyFilters(filterData) {
		this.selectedFilters = filterData || {};

		// Through rerun() like every other click that changes the results: it is the one place that records what
		// the results on screen answer for, and that decides whether there is anything to search at all. Going
		// straight to performSearch() left both untouched, so clearing the box and then ticking a facet searched
		// for nothing and showed an empty table where the browse view belongs.
		this.rerun();
	}

	/**
	 * Adds one distributor's copy of a product. The copy is the choice being made - price, stock and pack size are
	 * all per-distributor - so the copy is what goes over, never the union group the row stands for.
	 *
	 * Left open afterwards: finding a product is the expensive part of this modal, and closing on the first add
	 * made a second one start over. The screen behind acts on each add as it arrives, so it is already up to date
	 * by the time the modal is dismissed - and it is that screen, not this one, that reports the copy as added.
	 */
	addProduct(distributor) {
		this.app.globalSearchAdd.emit(distributor);
	}

	canAdd() {
		return !!this.app.globalSearchAdd.observers?.length;
	}

	/**
	 * Opens the product editor on one distributor's copy. The copy is what is being edited - its price, its SKU and
	 * its accessories are its own - so the product id comes from the supplier's line rather than from the union group
	 * the row is named after.
	 */
	editProduct(id) {
		if (!id) return;
		this.productEditModal.open({ id });
	}

	/**
	 * The editor closes with the saved product, or with nothing when it was dismissed. A save - which is also how a
	 * delete is applied - can change anything the results show, so they are searched again rather than patched.
	 */
	productEditModalClosed(data) {
		if (data) this.refreshResults();
	}

	/**
	 * Searches again for what is already on screen, ignoring what was last fetched. Every tab's cache goes, not just
	 * the products one: stock lists the same records, and the badge counts were taken against the old catalogue too.
	 *
	 * A product panel open over the results was edited from its own Suppliers tab, and a record panel's stock has just
	 * been reserved or released, so either is put back once the results land - see reopenProduct and reopenRecord.
	 */
	private refreshResults(): void {
		this.tabCache = {};

		const product = this.selectedProduct;
		const record = this.selectedRecord;

		if (this.canSearch) {
			this.reopenProduct = product;
			this.reopenRecord = record;
			this.rerun();
			return;
		}

		// Nothing searched for behind it, so there is no result set to wait on - only the panel itself to re-read.
		if (product) this.selectProduct(product);
		else if (record) this.loadRecordDetail();
	}

	get searchTerm() {
		return this.searchQuery.trim();
	}

	// Says what the box searches on the tab that's open.
	get searchPlaceholder() {
		const placeholders = {
			products: 'Search products',
			stock: 'Search stock',
			customers: 'Search customers',
			users: 'Search users',
			journal: 'Search notes, documents and reports'
		};
		return placeholders[this.activeTab] || 'Search';
	}

	// Customers and users list the whole org when nothing is typed; products and stock are far too large for that.
	/**
	 * Currency for the converted totals column. The search response states which currency it converted into, so
	 * that wins over orgInfo - which isn't guaranteed to have loaded by the time results land.
	 */
	get currencyId() {
		return this.resultsCurrencyId || this.app.orgInfo?.primary_currency_id || null;
	}

	// The journal lists the latest entries when nothing is typed, the same way customers and users list everyone.
	get tabListsAll() {
		return this.activeTab === 'customers' || this.activeTab === 'users' || this.activeTab === 'journal';
	}

	// A search needs either a long enough term or somewhere drilled to; anything less isn't worth a round trip.
	get canSearch() {
		return this.tabListsAll || this.searchTerm.length >= this.minSearchLength || this.hasScope;
	}

	// "All customers · 12" when nothing is narrowing it, "customers · 3 results" once something is.
	get resultsLabel() {
		return (!this.resultsTerm && !this.hasFilters) ? 'All ' + this.activeTab : this.activeTab;
	}

	/**
	 * The term the results on screen were found with. Everything that describes those results - the highlighting,
	 * the header, "nothing found for ..." - reads this rather than the box, which can hold something new that hasn't
	 * been searched for yet.
	 */
	get resultsTerm() {
		return this.submittedTerm || '';
	}

	// Until a search has been asked for, the modal shows the browse view instead of a result table. Keyed off what
	// was last submitted rather than the box, so the view doesn't flip while a new term is being typed.
	get browsing() {
		return this.submittedTerm === null && !this.hasScope;
	}

	/**
	 * Switching tab runs the same term against the new tab - unless that tab already holds results for it, in which
	 * case they go straight back up. Its filters and scope don't carry over.
	 */
	selectTab(tab) {
		if (tab.disabled || tab.id === this.activeTab) return;

		// Where the tab being left is paginated, it comes back to the page it was on rather than to the top.
		if (this.tabCache[this.activeTab]) this.tabCache[this.activeTab].page = this.pagination.page;

		this.activeTab = tab.id;

		// Whatever the tab being left still has in flight no longer answers for what's on screen.
		this.searchSeq++;

		// The selection is deliberately left alone: it persists across tabs and simply isn't applied on a tab
		// that has no such filter. The search term carries across too.

		// The drill-down belongs to the classification tree, which products and stock share. Tabs without a tree
		// simply aren't scoped by it; the scope is held aside and restored on the way back.
		this.scope = this.showsCategoryTree
			? this.treeScope
			: { segment: null, category: null, subcategories: [] };

		this.facets = null;
		this.closeRecord();
		this.closeProduct();

		// Every badge already reflects the current term, so the tab being left keeps its count as it is.
		this.buildFilterSections();

		// Products and stock share the tree but not its counts, so moving between them reloads it.
		this.loadBrowse();

		// The results this tab last showed still answer for what's searched, so there's nothing to fetch.
		if (this.canSearch && this.restoreResults()) return;

		this.rerun();
	}

	get showsCategoryTree() {
		return this.tabsWithCategoryTree.indexOf(this.activeTab) !== -1;
	}

	get tabImplemented() {
		return this.tabsImplemented.indexOf(this.activeTab) !== -1;
	}

	/**
	 * The filter sections for the active tab, built from the facet counts the backend returned. Facets describe a
	 * result set, so there's nothing to show until a search has run.
	 *
	 * Held as a field rather than a getter: a getter hands Angular a new array every change-detection cycle, which
	 * re-creates the checkbox elements underneath the pointer and swallows the click.
	 */
	private buildFilterSections() {
		this.filterSections = this.composeFilterSections();
		// Recorded before the chips are built, so a value still in the facets refreshes its own remembered name.
		this.rememberFilterLabels();
		this.filterChips = this.composeFilterChips();
	}

	/**
	 * The active tab's selected facet values, as chips beside the scope ones so it's visible what is narrowing the
	 * results without opening the panel. Only this tab's sections are listed - a choice made on another tab isn't
	 * applied here, so showing it would claim a filter that isn't in effect.
	 *
	 * Labels come from the facet sections, then the section's own label for a single-checkbox section, then the last
	 * one seen for that value. Those fallbacks matter: facets describe a result set, so a search that matches nothing
	 * returns none at all - and the raw value is a catalogue id for a distributor, or just `yes` for a flag.
	 */
	private composeFilterChips() {
		const chips = [];

		this.activeFilterIds.forEach(sectionId => {
			const section = this.filterSections.find(s => s.id === sectionId);

			(this.selectedFilters[sectionId] || []).forEach(value => {
				const entry = this.sectionValues(section).find(v => v.value === value);
				const remembered = this.filterLabels[sectionId + ':' + value];

				chips.push({
					section_id: sectionId,
					value,
					label: entry?.label || section?.flagLabel || remembered?.label || value,
					color: entry?.color || remembered?.color || null
				});
			});
		});

		return chips;
	}

	/**
	 * Keeps the name and colour of every facet value the panel has offered, so a chip can still say what it is once
	 * the value has dropped out of the facets. A value can only be selected after appearing here at least once.
	 */
	private rememberFilterLabels(): void {
		this.filterSections.forEach(section => {
			this.sectionValues(section).forEach(value => {
				this.filterLabels[section.id + ':' + value.value] = { label: value.label, color: value.color || null };
			});
		});
	}

	// A section's selectable values, with any groups opened out into the children that are actually selected.
	private sectionValues(section) {
		const values = [];
		(section?.values || []).forEach(value => {
			if (Array.isArray(value.children)) values.push(...value.children);
			else values.push(value);
		});
		return values;
	}

	// Drops one facet value, leaving the rest of the selection - and the other tabs' choices - alone.
	removeFilter(chip): void {
		const next = { ...this.selectedFilters };
		const values = (next[chip.section_id] || []).filter(value => value !== chip.value);

		if (values.length) {
			next[chip.section_id] = values;
		} else {
			delete next[chip.section_id];
		}

		this.applyFilters(next);
	}

	private composeFilterSections() {
		if (this.browsing || !this.facets) return [];

		if (this.activeTab === 'customers') {
			return [
				{ id: 'labels', name: 'Labels', hasSearch: true, values: this.toValues(this.facets.labels) },
				this.flagSection('openQuotes', 'Quotes', 'Has open quotes', this.facets.open_quotes)
			];
		}

		if (this.activeTab === 'journal') {
			return [
				{ id: 'contentTypes', name: 'Content type', expandable: false, values: this.journalContentTypeValues() },
				{ id: 'recordSources', name: 'Record source', expandable: false, values: this.journalSourceValues() },
				{ id: 'formats', name: 'Format', expandable: false, values: this.journalFormatValues() },
				{ id: 'authors', name: 'Added by', hasSearch: true, values: this.toValues(this.facets.authors) },
				{ id: 'customers', name: 'Customer', hasSearch: true, values: this.toValues(this.facets.customers) },
				{ id: 'projects', name: 'Project', hasSearch: true, values: this.toValues(this.facets.projects) }
			];
		}

		if (this.activeTab === 'users') {
			return [
				{ id: 'roles', name: 'Role', values: this.toValues(this.facets.roles) },
				{ id: 'documentTypes', name: 'Document type', expandable: false, values: this.documentTypeValues() }
			];
		}

		if (this.activeTab === 'stock') {
			return [
				{ id: 'warehouses', name: 'Warehouse or site', hasSearch: true, values: this.toValues(this.facets.warehouses) },
				{ id: 'serialised', name: 'Serialised', expandable: false, values: this.toValues(this.facets.serialised) },
				{ id: 'allocation', name: 'Allocation', expandable: false, values: this.toValues(this.facets.allocation) },
				this.flagSection('freeStock', 'Free stock', 'Has free stock', this.facets.free_stock)
			];
		}

		return [
			this.flagSection('inStock', 'Availability', 'In stock', this.facets.in_stock),
			this.flagSection('discontinued', 'Status', 'Include Discontinued', this.facets.discontinued),
			{ id: 'brands', name: 'Brands', hasSearch: true, values: this.toValues(this.facets.brands) },
			{ id: 'distributors', name: 'Distributor', hasSearch: true, values: this.toDistributorValues(this.facets.distributors) }
		];
	}

	/**
	 * Opens the product panel over the results, on a product id - the row's main product, or the accessory that was
	 * clicked inside the panel. The backend takes it to its union group, so the panel still lists every distributor
	 * behind the row rather than the single copy that was named.
	 */
	selectProduct(id) {
		if (!id) return;

		this.selectedProduct = id;
		this.productDetail = null;
		this.productDetailLoading = true;
		this.productSectionsLoading = [];
		this.productSectionsLoaded = [];

		this.api.globalSearch.productDetail(
			id,
			(data) => {
				// Ignore a response for a product that is no longer open.
				if (this.selectedProduct !== id) return;
				this.productDetail = data;
				this.productDetailLoading = false;
			},
			(error) => {
				if (this.selectedProduct !== id) return;
				this.productDetailLoading = false;
				this.closeProduct();
				this.app.notifications.showDanger(error.message);
			}
		);
	}

	/**
	 * Loads the tab a viewer has just opened, once per product. The response is merged into the payload the panel
	 * already holds, so the panel reads the same whether a section arrived with it or after it.
	 */
	loadProductSection(section) {
		if (!section || !this.selectedProduct) return;
		if (this.productSectionsLoaded.indexOf(section) !== -1) return;
		if (this.productSectionsLoading.indexOf(section) !== -1) return;

		const id = this.selectedProduct;
		this.productSectionsLoading = this.productSectionsLoading.concat(section);

		const done = () => {
			this.productSectionsLoading = this.productSectionsLoading.filter(item => item !== section);
		};

		this.api.globalSearch.productSection(
			id,
			section,
			(data) => {
				// Ignore a response for a product that is no longer open.
				if (this.selectedProduct !== id) return;
				done();
				this.productSectionsLoaded = this.productSectionsLoaded.concat(section);
				this.productDetail = { ...this.productDetail, ...data };
			},
			(error) => {
				if (this.selectedProduct !== id) return;
				done();
				this.app.notifications.showDanger(error.message);
			}
		);
	}

	closeProduct() {
		this.selectedProduct = null;
		this.productDetail = null;
		this.productDetailLoading = false;
		this.productSectionsLoading = [];
		this.productSectionsLoaded = [];
	}

	get showsProductDetail() {
		return this.activeTab === 'products' && !!this.selectedProduct;
	}

	// Opens the detail panel for a result row, or closes it if the same row is clicked again.
	selectRecord(record) {
		if (this.recordKey(this.selectedRecord) === this.recordKey(record)) {
			this.closeRecord();
			return;
		}

		this.selectedRecord = record;
		this.loadRecordDetail();
	}

	closeRecord() {
		this.selectedRecord = null;
		this.recordDetail = null;
	}

	/**
	 * Stock rows are an item held across warehouses rather than a single row id, so they key on the group. Journal
	 * rows come from several tables whose ids overlap, so they key on their source as well.
	 */
	private recordKey(record) {
		if (!record) return null;
		if (record.item_key !== undefined) return record.item_key;
		if (record.key !== undefined) return record.key;
		return record.id;
	}

	get showsRecordDetail() {
		return this.tabsWithRecordPanel.indexOf(this.activeTab) !== -1 && !!this.selectedRecord;
	}

	get recordKind() {
		if (this.activeTab === 'journal') return this.journalKindLabel(this.selectedRecord);
		return { users: 'User', stock: 'Stock item' }[this.activeTab] || 'Customer';
	}

	// Stock leads with the manufacturer and model, the way a product row reads; the others carry a name.
	get recordTitle() {
		const record = this.selectedRecord;
		if (!record) return '';

		if (this.activeTab === 'journal') return this.journalTitle(record);
		if (this.activeTab !== 'stock') return record.name;

		const parts = [record.manufacturer_name, record.manufacturer_sku || record.model || record.warehouse_sku];
		return parts.filter(part => !!part).join(' ') || record.short_description;
	}

	get recordSubtitle() {
		if (this.activeTab === 'stock') return this.selectedRecord?.short_description || '';
		// The journal panel shows its author with an avatar in its own body, which the plain subtitle can't carry.
		if (this.activeTab === 'journal') return '';
		return this.recordDetail?.subtitle || '';
	}

	// Route for the panel's "open the whole record" link. Users and stock have no screen of their own to open.
	get recordLink() {
		if (!this.selectedRecord) return null;
		if (this.activeTab === 'journal') {
			const link = this.recordDetail?.link || this.selectedRecord.link;
			return link?.length ? ['/', this.app.org, ...link] : null;
		}
		if (this.activeTab !== 'customers') return null;
		return ['/', this.app.org, 'customer', this.selectedRecord.id];
	}

	get recordLinkQuery() {
		if (this.activeTab !== 'journal') return null;
		return this.recordDetail?.link_query || this.selectedRecord?.link_query || null;
	}

	/**
	 * The panel's roll-up line, with the quoted total appended the way the design shows it. Composed here rather
	 * than in the backend so the amount goes through the same currency pipe as everything else.
	 */
	get recordSummary() {
		const detail = this.recordDetail;
		if (!detail) return '';

		if (this.activeTab === 'stock') {
			const availability = detail.availability || {};
			const parts = [
				DecimalPipe.transform(availability.free, 0, 4, true) + ' free',
				DecimalPipe.transform(availability.allocated, 0, 4, true) + ' allocated',
				DecimalPipe.transform(availability.reserved, 0, 4, true) + ' reserved'
			];

			const names = availability.warehouse_names || [];
			if (names.length) parts.push(names.join(', '));

			return parts.join(' · ');
		}

		if (!detail.quoted_total) return detail.summary;

		const quoted = CurrencyFlexiblePipe.transform(detail.quoted_total, detail.currency_id);
		return detail.summary + ' · ' + quoted + ' quoted';
	}

	// Stock leads on the quantity held rather than the active section's count.
	get recordBigValue() {
		if (this.activeTab !== 'stock' || !this.recordDetail) return null;
		return DecimalPipe.transform(this.recordDetail.availability?.on_hand, 0, 4, true);
	}

	get recordBigLabel() {
		return this.activeTab === 'stock' && this.recordDetail ? 'units on hand' : null;
	}

	get recordLinkLabel() {
		if (this.activeTab === 'journal') return 'Open ' + this.journalSourceLabel(this.selectedRecord).toLowerCase();
		return this.activeTab === 'customers' ? 'Open customer record' : '';
	}

	// Only a stock item can be reserved; a customer or user panel has nothing to offer against its groups.
	get recordGroupActionLabel() {
		return this.activeTab === 'stock' ? 'Reserve' : '';
	}

	get recordLineActions() {
		return this.activeTab === 'stock' ? this.reservationLineActions : [];
	}

	/**
	 * Reserve stock in the warehouse a Locations group heads, on the same modal the warehouse's own stocked item
	 * list uses. The group carries the SKU held there and what is free of it, which is what the reservation is
	 * against - the backend re-checks both against the organisation before anything is written.
	 */
	reserveStock(group) {
		this.reserveItemsModal.open({
			warehouse: group.warehouse_id,
			product: this.reservationProduct(group)
		});
	}

	/**
	 * A saved reservation changes the free and reserved figures the panel is showing and the ones the stock row
	 * behind it reports, so the search is run again rather than only the panel re-read.
	 */
	reserveItemsModalClosed(data) {
		if (data) this.refreshResults();
	}

	// Edit or drop the reservation a Reservations line describes.
	recordLineAction(event) {
		const line = event?.line;
		if (!line?.reservation_id) return;

		if (event.action === 'edit') this.editReservation(event.group, line);
		else if (event.action === 'delete') this.deleteReservation(line);
	}

	private editReservation(group, line) {
		// The modal re-reads the reservation and the stock behind it, so what's passed here is only what it opens on.
		this.reserveItemsModal.open({
			reservationId: line.reservation_id,
			warehouse: group?.warehouse_id,
			product: this.reservationProduct(group)
		});
	}

	/**
	 * What the reserve modal needs from a warehouse block, in the shape the warehouse's own stocked item list hands
	 * it. A reservation is written against a warehouse SKU, so the code travels with it rather than being worked
	 * back out of the row, and the modal names it. Safe from this panel because a stock row is one SKU: a product
	 * can only be mapped to one, which is what makes sku_free the whole of what the block has free.
	 */
	private reservationProduct(group) {
		return {
			sku_id: group?.sku_id,
			warehouse_sku: group?.sku,
			quantity_available: group?.sku_free
		};
	}

	private deleteReservation(line) {
		this.app.dialog.popHtml('Delete reservation', 'Are you sure you want to delete this reservation?', ['Cancel', '-Delete'], btn => {
			if (btn !== 1) return;

			this.api.inventory.releaseReservation({ reservation_id: line.reservation_id, reason: 'manual' }, () => {
				this.app.notifications.showSuccess('Reservation deleted.');
				this.refreshResults();
			}, error => {
				this.app.notifications.showDanger(error.message);
			});
		});
	}

	get orgAlias() {
		return this.app.org;
	}

	private loadRecordDetail(): void {
		const record = this.selectedRecord;
		const key = this.recordKey(record);
		if (key === null || key === undefined) return;

		this.recordDetail = null;
		this.recordDetailLoading = true;

		// Ignore a response for a row that is no longer selected.
		const done = (data) => {
			if (this.recordKey(this.selectedRecord) !== key) return;
			this.recordDetail = data;
			this.recordDetailLoading = false;
		};
		const fail = () => {
			if (this.recordKey(this.selectedRecord) !== key) return;
			this.recordDetail = null;
			this.recordDetailLoading = false;
		};

		// A journal row is keyed by its source and id, and the entry is read back in full - the row only carries a snippet.
		if (this.activeTab === 'journal') {
			this.api.globalSearch.journalDetail(record.key, done, fail);
			return;
		}

		// A stock row is a set of inventory SKUs, so the panel is loaded by those rather than by a record id.
		if (this.activeTab === 'stock') {
			this.api.globalSearch.stockDetail({ sku_ids: record.sku_ids || [] }, done, fail);
			return;
		}

		const load = this.activeTab === 'users'
			? this.api.globalSearch.userDetail
			: this.api.globalSearch.customerDetail;

		load.call(this.api.globalSearch, record.id, done, fail);
	}

	// Browsing into a category scopes the search to it rather than typing its name as a keyword.
	browseCategory(category) {
		this.scope = { segment: category.segment, category: category.category, subcategories: [] };
		this.treeScope = this.scope;
		this.rerun();
	}

	// Each tab reports how many matches it found for the current search.
	/**
	 * A tab's badge. Counting stops at a cap - the products keyword match can't use an index, so an uncapped count
	 * costs as much as the search - which makes a saturated number a floor rather than a total, and it says so.
	 */
	tabCount(tabId) {
		const count = this.tabTotals[tabId] || 0;
		return this.tabApproximate[tabId] ? count + '+' : count;
	}

	// "2 projects · 2 sites · 2 quotes" - only the kinds the customer actually has.
	customerUsage(customer) {
		return this.summarise(customer, {
			projects: ['project', 'projects'],
			sites: ['site', 'sites'],
			quotes: ['quote', 'quotes']
		});
	}

	// "2 quotes · 2 projects · 1 PO" - only the document types the user actually has.
	documentSummary(user) {
		const labels = {
			quotes: ['quote', 'quotes'],
			projects: ['project', 'projects'],
			pos: ['PO', 'POs'],
			requests: ['request', 'requests'],
			invoices: ['invoice', 'invoices']
		};

		return this.summarise(user, labels);
	}

	private summarise(record, labels) {
		return Object.keys(labels)
			.filter(key => record[key] > 0)
			.map(key => record[key] + ' ' + labels[key][record[key] === 1 ? 0 : 1])
			.join(' · ');
	}

	private toValues(facet) {
		return (facet || []).map(entry => ({
			value: entry.value,
			label: entry.label || entry.value,
			count: entry.cnt,
			color: entry.color
		}));
	}

	private toDistributorValues(facet) {
		return (facet || []).map(entry => ({ value: entry.catalogue_id, label: entry.name, count: entry.cnt }));
	}

	/**
	 * A single-checkbox section, e.g. "In stock" or "Has open quotes". Its one value is just the flag `yes`, so the
	 * label is kept on the section as well: facets describe a result set, so a search matching nothing returns a
	 * count of zero and no checkbox at all, and without this the chip would have nothing to read but `yes`.
	 */
	private flagSection(id, name, label, count) {
		return {
			id,
			name,
			expandable: false,
			flagLabel: label,
			values: count ? [{ value: 'yes', label, count }] : []
		};
	}

	private documentTypeValues() {
		const counts = this.facets?.document_types || {};
		return [
			{ value: 'quotes', label: 'Quotes', count: counts.quotes },
			{ value: 'pos', label: 'POs', count: counts.pos },
			{ value: 'requests', label: 'Requests', count: counts.requests },
			{ value: 'invoices', label: 'Invoices', count: counts.invoices }
		].filter(v => v.count !== undefined);
	}

	// "Note", "Document link", "Report" - what a journal entry is, for its badge and the panel's heading.
	journalKindLabel(entry) {
		if (!entry) return '';
		if (entry.kind === 'report') return 'Report';
		if (entry.kind === 'document') return entry.file_name ? 'File' : 'Document link';
		return 'Note';
	}

	journalKindIcon(entry) {
		if (entry?.kind === 'report') return 'wq-clipboard';
		if (entry?.kind === 'document') return entry.file_name ? 'wq-document-check' : 'wq-link';
		return 'wq-notes';
	}

	// The record source as a singular name, e.g. "Sales Order".
	journalSourceLabel(entry) {
		return (this.journalSources[entry?.record_type] || [])[0] || '';
	}

	// The record source's menu icon, e.g. "wq-handshake" for a quote.
	journalSourceIcon(entry) {
		return (this.journalSources[entry?.record_type] || [])[2] || '';
	}

	// The badge class for the record source, from the colour of the group it belongs to, e.g. "badge-primary".
	journalSourceBadge(entry) {
		const group = this.journalSourceGroups.find(g => g.sources.indexOf(entry?.record_type) !== -1);
		return 'badge-' + (group ? group.color : 'secondary');
	}

	/**
	 * A document or report is named; a note usually isn't, so the panel heads it with what it was written on - the
	 * same thing the row's meta line leads with.
	 */
	journalTitle(entry) {
		if (!entry) return '';
		if (entry.title) return entry.title;
		const record = [entry.ref, entry.record_title].filter(part => !!part).join(' · ');
		return this.journalKindLabel(entry) + ' on ' + (record || this.journalSourceLabel(entry).toLowerCase());
	}

	// "S-SO-001 · Demo cinema equipment supply" - the record a journal row is kept on.
	journalRecord(entry) {
		return [entry?.ref, entry?.record_title].filter(part => !!part).join(' · ');
	}

	// The record, then whichever of its customer and project the row carries.
	journalMeta(entry) {
		const parts = [this.journalRecord(entry)];
		if (entry?.customer_id && entry.record_type !== 'customer') parts.push(entry.customer_name);
		if (entry?.project_id && entry.record_type !== 'project') parts.push(entry.project_title || entry.project_no);
		return parts.filter(part => !!part).join(' · ');
	}

	// A report prints to PDF through the same endpoint the reports list opens it with.
	openJournalReport(entry) {
		if (!entry?.source_id) return;
		const timestamp = new Date().toISOString();
		window.open('/api/' + this.app.org + '/content/print_report?id=' + entry.source_id + '&timestamp=' + encodeURIComponent(timestamp));
	}

	private journalContentTypeValues() {
		const labels = { note: 'Notes', document: 'Documents', report: 'Reports' };
		return (this.facets?.content_types || []).map(entry => ({ value: entry.value, label: labels[entry.value] || entry.value, count: entry.cnt }));
	}

	private journalFormatValues() {
		const labels = { pdf: 'PDF', link: 'Other links' };
		return (this.facets?.formats || []).map(entry => ({ value: entry.value, label: labels[entry.value] || entry.value, count: entry.cnt }));
	}

	/**
	 * The record sources, grouped by the area of the app they belong to. Only the sources the backend offered are
	 * listed - it leaves out any the user can't read - and a group left with nothing in it isn't shown at all.
	 */
	private journalSourceValues() {
		const counts = {};
		(this.facets?.record_sources || []).forEach(entry => counts[entry.value] = entry.cnt);

		return this.journalSourceGroups
			.map(group => {
				const children = group.sources
					.filter(source => counts[source] !== undefined)
					.map(source => ({ value: source, label: this.journalSources[source][1], count: counts[source] }));

				return {
					value: 'group:' + group.id,
					label: group.label,
					count: children.reduce((total, child) => total + child.count, 0),
					children
				};
			})
			.filter(group => group.children.length);
	}

}
